import { Button } from "../../ui/button";
import { UserCheck, WifiOff, Scan, Router, Trash2 } from "lucide-react";
import { Device } from "../interfaces/EndpointInterfaces";
import ConfirmationDialog from "./ConfirmationDialog";
import FriendlyNameDialog from "./FriendlyNameDialog";
import ActionStatus from "./ActionStatus";
import { useState } from "react";
import GatewayDialog from "./GatewayDialog";
import { getIsLinux } from "./helper";
import { useToast } from "../../../hooks/use-toast";
import { getCredentialsList, revealCredentialPassword } from "../Credentials/Api/credentialapi";
import { CredentialInterface } from "../Credentials/interfaces/credentialinterface";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";

interface ActionButtonsProps {
  onFriendlyName?: (deviceId: string, friendlyName: string) => void;
  onDisableConnectivity?: (enabled: boolean) => void;
  onScan?: () => void;
  onGateway?: (deviceId: string, selectedSubnets: string[], label: string) => void;
  onDelete?: () => void;
  tenantName?: string;
  enabledButtons?: {
    friendlyName?: boolean;
    disableConnectivity?: boolean;
    scan?: boolean;
    gateway?: boolean;
    delete?: boolean;
    tags?: boolean;
  };
  selectedDevice?: Device;
  resetValue: () => void
}

export default function ActionButtons({
  onFriendlyName,
  onDisableConnectivity,
  onScan,
  onGateway,
  onDelete,
  tenantName,
  enabledButtons = {},
  selectedDevice,
  resetValue
}: ActionButtonsProps) {
  const { toast } = useToast();

  const [showDisableConfirmation, setShowDisableConfirmation] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showCopyCredentialsConfirmation, setShowCopyCredentialsConfirmation] = useState(false);
  const [showFriendlyNameDialog, setShowFriendlyNameDialog] = useState(false);
  const [showGatewayDialog, setShowGatewayDialog] = useState(false);
  const [deleteCheckLoading, setDeleteCheckLoading] = useState(false);
  const [copyingCredentials, setCopyingCredentials] = useState(false);
  const [credentialsToCopy, setCredentialsToCopy] = useState<CredentialInterface[]>([]);

  const [actionStatus, setActionStatus] = useState<{
    show: boolean;
    message: string;
    type: "success" | "error" | "warning";
  }>({
    show: false,
    message: "",
    type: "success"
  });

  const isWindowsDevice = (device?: Device | null) => {
    const normalizedOs = `${device?.os || device?.OperatingSystem || ""}`.toLowerCase();
    return normalizedOs.includes("windows");
  };

  const isCredentialAutoRotateEnabled = (credential: CredentialInterface) =>
    Boolean(
      credential.lastRotatedDate ??
      credential.isAutoRotate ??
      credential.autoRotate ??
      credential.autoRotationEnabled ??
      credential.isAutoRotateEnabled
    );

  const openDeleteConfirmation = () => {
    setShowCopyCredentialsConfirmation(false);
    setShowDeleteConfirmation(true);
  };

  const handleDeleteClick = async () => {
    if (!selectedDevice) {
      return;
    }

    if (!isWindowsDevice(selectedDevice) || !tenantName?.trim()) {
      setShowDeleteConfirmation(true);
      return;
    }

    setDeleteCheckLoading(true);
    try {
      const response = await getCredentialsList(tenantName, selectedDevice.id);
      const deviceCredentials = Array.isArray(response)
        ? (response as CredentialInterface[])
        : [];
      const hasAutoRotateCredential = deviceCredentials.some(isCredentialAutoRotateEnabled);

      if (hasAutoRotateCredential) {
        setCredentialsToCopy(deviceCredentials);
        setShowCopyCredentialsConfirmation(true);
        return;
      }

      setShowDeleteConfirmation(true);
    } catch (error: any) {
      toast({
        title: "Credential check failed",
        description:
          typeof error === "string"
            ? error
            : error?.message || "Unable to verify endpoint credentials before deletion.",
        variant: "destructive",
      });
      setShowDeleteConfirmation(true);
    } finally {
      setDeleteCheckLoading(false);
    }
  };

  const handleCopyCredentialsAndContinue = async () => {
    if (!selectedDevice || !tenantName?.trim() || !credentialsToCopy.length) {
      openDeleteConfirmation();
      return;
    }

    setCopyingCredentials(true);
    try {
      const credentialExports = await Promise.all(
        credentialsToCopy.map(async (credential) => {
          const label = `${credential.credentialLabel || ""}`.trim() || "N/A";
          let password = "[Password unavailable]";

          if (label !== "N/A") {
            try {
              const revealedPassword = await revealCredentialPassword({
                computerId: selectedDevice.id,
                tenantName,
                label,
              });
              password = revealedPassword || "[Empty password]";
            } catch (error) {
              password = "[Password unavailable]";
            }
          }

          return [
            `Label: ${label}`,
            `Username: ${credential.credentialUsername || "N/A"}`,
            `Protocol: ${credential.protocol || credential.protocolType || "N/A"}`,
            `Auto Rotate: ${isCredentialAutoRotateEnabled(credential) ? "Enabled" : "Disabled"}`,
            `Password: ${password}`,
          ].join("\n");
        })
      );

      const clipboardPayload = [
        `Device: ${selectedDevice.name || selectedDevice.id}`,
        `Device ID: ${selectedDevice.id}`,
        "",
        ...credentialExports.map((entry, index) => `Credential ${index + 1}\n${entry}`),
      ].join("\n\n");

      await navigator.clipboard.writeText(clipboardPayload);

      toast({
        title: "Credentials copied",
        description: `${credentialsToCopy.length} credential(s) copied to the clipboard before deletion.`,
      });

      openDeleteConfirmation();
    } catch (error: any) {
      toast({
        title: "Copy failed",
        description:
          typeof error === "string"
            ? error
            : error?.message || "Unable to copy credentials to the clipboard.",
        variant: "destructive",
      });
    } finally {
      setCopyingCredentials(false);
    }
  };

  return (
    <div className="flex items-center space-x-2">
      <Button
        variant="outline"
        size="sm"
        disabled={!enabledButtons.friendlyName}
        onClick={() => setShowFriendlyNameDialog(true)}
        data-testid="button-friendly-name"
        className="flex items-center space-x-2"
      >
        <UserCheck className="w-4 h-4" />
        <span>Friendly Name</span>
      </Button>

      <Button
        variant="outline"
        size="sm"
        disabled={!enabledButtons.disableConnectivity}
        onClick={() => setShowDisableConfirmation(true)}
        data-testid="button-disable-connectivity"
        className="flex items-center space-x-2"
      >
        <WifiOff className="w-4 h-4" />
        <span>{selectedDevice?.isNebulaEnabled ? "Disable Connectivity" : "Enable Connectivity"} {selectedDevice?.isNebulaEnabled}</span>
      </Button>

      <Button
        variant="outline"
        size="sm"
        disabled={!enabledButtons.scan}
        onClick={() => {
          console.log('Scan clicked');
          setActionStatus({
            show: true,
            message: "Scan Started",
            type: "success"
          });
          onScan?.();
        }}
        data-testid="button-scan"
        className="flex items-center space-x-2"
      >
        <Scan className="w-4 h-4" />
        <span>Scan</span>
      </Button>
      {getIsLinux(selectedDevice?.os || "") && <Button
        variant="outline"
        size="sm"
        disabled={!enabledButtons.gateway}

        onClick={() => setShowGatewayDialog(true)}
        data-testid="button-gateway"
        className="flex items-center space-x-2"
      >
        <Router className="w-4 h-4" />
        <span>Gateway</span>
      </Button>}



      <Button
        variant="destructive"
        size="sm"
        disabled={!enabledButtons.delete || deleteCheckLoading}
        onClick={handleDeleteClick}
        data-testid="button-delete"
        className="flex items-center space-x-2"
      >
        <Trash2 className="w-4 h-4" />
        <span>{deleteCheckLoading ? "Checking..." : "Delete"}</span>
      </Button>


      <ConfirmationDialog
        open={showDisableConfirmation}
        onOpenChange={setShowDisableConfirmation}
        title={selectedDevice?.isNebulaEnabled ? "Disable Connectivity?" : "Enable Connectivity?"}
        description={selectedDevice?.isNebulaEnabled ? "Once connectivity is disabled, users will no longer be able to connect to the machine. Do you wish to continue?" : "Once connectivity is enabled, users will be able to connect to the machine."}
        confirmText="Confirm"
        cancelText="Cancel"
        variant={selectedDevice?.isNebulaEnabled ? "warning" : "success"}
        onConfirm={() => {
          const enable = !selectedDevice?.isNebulaEnabled;
          console.log('Connectivity disabled');
          onDisableConnectivity?.(enable);
        }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showCopyCredentialsConfirmation}
        onOpenChange={setShowCopyCredentialsConfirmation}
      >
        <DialogContent className="max-w-md" data-testid="dialog-copy-credentials-before-delete">
          <DialogHeader className="space-y-4">
            <DialogTitle className="text-lg font-semibold text-slate-800 dark:text-slate-100">
              Copy credentials before deleting?
            </DialogTitle>
            <DialogDescription className="leading-relaxed text-slate-600 dark:text-slate-400">
              This Windows machine has credential auto-rotation enabled. Copy the device credentials before you continue deleting {selectedDevice?.name || "this device"}.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="mt-6 gap-2 sm:flex-wrap">
            <Button
              variant="outline"
              onClick={() => setShowCopyCredentialsConfirmation(false)}
              disabled={copyingCredentials}
              className="w-full sm:w-auto"
              data-testid="button-cancel-copy-before-delete"
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={openDeleteConfirmation}
              disabled={copyingCredentials}
              className="w-full whitespace-normal text-center text-white bg-green-600 border-green-600 hover:bg-green-700 hover:border-green-700 sm:w-auto"
              data-testid="button-skip-copy-before-delete"
            >
              I Have Copied
            </Button>

          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmationDialog
        open={showDeleteConfirmation}
        onOpenChange={setShowDeleteConfirmation}
        title="Delete Computer"
        description={`Are you sure, you want to delete this computer - ${selectedDevice?.name || 'this device'}?`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="destructive"
        onConfirm={() => {
          console.log('Device deleted');
          onDelete?.();
        }}
      />

      {/* Friendly Name Dialog */}
      <FriendlyNameDialog
        open={showFriendlyNameDialog}
        onOpenChange={setShowFriendlyNameDialog}
        device={selectedDevice || null}
        onSave={(deviceId, friendlyName) => {
          console.log('Friendly name updated:', deviceId, friendlyName);
          onFriendlyName?.(deviceId, friendlyName);
        }}
      />

      {/* Action Status Notification */}
      <ActionStatus
        show={actionStatus.show}
        message={actionStatus.message}
        type={actionStatus.type}
        onClose={() => setActionStatus({ ...actionStatus, show: false })}
      />

      <GatewayDialog
        open={showGatewayDialog}
        onOpenChange={setShowGatewayDialog}
        device={selectedDevice || null}
        onSave={(deviceId, selectedSubnets, label) => {
          onGateway?.(deviceId, selectedSubnets, label);
          resetValue?.();
        }}
        onCancel={() => {
          resetValue?.();
        }}
      />


    </div>
  );
}